<template>
  <div v-if="!loadingFirst || total > 0" class="associated-entities">
    <div class="font-weight-bold text-body-2">
      {{ $t("profile.associations") }}:
    </div>

    <div class="d-flex align-center flex-wrap mb-2">
      <span v-if="loadingFirst" class="text--disabled">Carregando…</span>

      <span v-else-if="!total" class="text--disabled">
        {{ $t("profile.noAssociations") }}
      </span>

      <template v-else>
        <!-- O nome precisa ser clicável por si: quem tem uma associação só não
        chega ao menu, que nem aparece nesse caso. -->
        <span
          :class="{ 'associated-entities__name--link': canOpen }"
          @click="handleSelect(firstEntity)"
        >
          {{ firstName }}
        </span>

        <v-menu
          v-if="remaining > 0"
          v-model="menu"
          :close-on-content-click="false"
          offset-y
          max-width="20rem"
          @input="onMenuToggle"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="text-none ml-2"
              color="primary"
              rounded
              outlined
              x-small
            >
              + {{ remaining }}
            </v-btn>
          </template>

          <v-card class="pa-2" max-width="20rem">
            <v-text-field
              v-model="search"
              placeholder="Buscar..."
              prepend-inner-icon="mdi-magnify"
              hide-details
              outlined
              dense
              clearable
              @input="onSearch"
            />

            <v-list dense class="associated-entities__list">
              <!-- Sem `disabled`: a lista de audiências é informativa, mas
              desabilitar pintava os nomes de cinza e deixava este menu com
              cara de inativo, diferente do de atendimento. Aqui tiramos só a
              interação — a leitura fica idêntica nos dois. -->
              <v-list-item
                v-for="item in items"
                :key="item._id"
                :ripple="canOpen"
                :class="{ 'associated-entities__item--static': !canOpen }"
                @click="handleSelect(item)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item v-if="!items.length && !loadingList">
                <v-list-item-content>
                  <v-list-item-title class="text--disabled">
                    Nenhum resultado
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <div class="d-flex align-center justify-center pt-1">
              <v-progress-circular
                v-if="loadingList"
                size="18"
                width="2"
                color="primary"
                indeterminate
              />

              <v-btn
                v-else-if="items.length < listTotal"
                class="text-none"
                small
                text
                color="primary"
                @click="loadPage()"
              >
                Carregar mais
              </v-btn>
            </div>
          </v-card>
        </v-menu>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import { getJurisdictions } from "@/services/jurisdictions";
import { getCourts } from "@/services/courts";
import { getCounters } from "@/services/counters";
import { getEntities } from "@/services/entities";

const PAGE_SIZE = 10;

export default {
  name: "AssociatedEntities",

  props: {
    // "attendances" usa a hierarquia do papel do usuário; "audiences" tem
    // entidades próprias.
    module: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      menu: false,
      search: "",
      items: [],
      // Total de associações do usuário, que define o "+ N" e não muda com a
      // busca. `listTotal` é o total da listagem atual, já filtrada.
      total: 0,
      listTotal: 0,
      firstEntity: null,
      loadingFirst: true,
      loadingList: false,
      offset: 0,
      searchDebounce: null,
    };
  },

  computed: {
    ...mapState(["user"]),
    ...mapGetters(["attendanceRole"]),

    /**
     * No módulo de atendimento a entidade depende do papel: administrador
     * responde por comarcas, gestor por varas e os demais por balcões.
     */
    entityKind() {
      if (this.module === "audiences") return "entity";
      if (this.attendanceRole === "admin") return "group";
      if (this.attendanceRole === "moderator") return "subgroup";

      return "desk";
    },

    firstName() {
      return this.firstEntity?.name || "";
    },

    remaining() {
      return Math.max(this.total - 1, 0);
    },

    canOpen() {
      // Entidade de audiência não tem modal reaproveitável aqui; a lista fica
      // apenas informativa.
      return this.entityKind !== "entity";
    },
  },

  mounted() {
    this.loadFirst();
  },

  methods: {
    fetcher(params) {
      const withPerson = { ...params };

      if (this.entityKind === "entity") {
        return getEntities({ ...withPerson, personIDs: [this.user._id] });
      }

      withPerson.personID = this.user._id;

      if (this.entityKind === "group") return getJurisdictions(withPerson);
      if (this.entityKind === "subgroup") return getCourts(withPerson);

      return getCounters(withPerson);
    },

    /**
     * O perfil abre com uma requisição mínima: só a primeira associação e o
     * total. A lista completa só é buscada se o usuário abrir o menu.
     */
    async loadFirst() {
      try {
        this.loadingFirst = true;

        const { data, headers } = await this.fetcher({
          limit: 1,
          offset: 0,
          sortBy: "name",
          sortOrder: "asc",
        });

        this.total = parseInt(headers["x-count"], 10) || data.length || 0;
        this.firstEntity = data?.[0] || null;
      } catch (error) {
        console.log("AssociatedEntities:loadFirst", error);
        this.total = 0;
      } finally {
        this.loadingFirst = false;
      }
    },

    onMenuToggle(opened) {
      if (!opened || this.items.length) return;

      this.resetList();
      this.loadPage();
    },

    resetList() {
      this.items = [];
      this.listTotal = 0;
      this.offset = 0;
    },

    onSearch() {
      clearTimeout(this.searchDebounce);

      this.searchDebounce = setTimeout(() => {
        this.resetList();
        this.loadPage();
      }, 350);
    },

    async loadPage() {
      try {
        this.loadingList = true;

        const { data, headers } = await this.fetcher({
          limit: PAGE_SIZE,
          offset: this.offset,
          sortBy: "name",
          sortOrder: "asc",
          ...(!!this.search && { search: this.search }),
        });

        this.items = [...this.items, ...data];
        this.offset += PAGE_SIZE;

        const count = parseInt(headers["x-count"], 10);
        // Só o total da listagem: sobrescrever o total geral zerava o "+ N"
        // quando a busca devolvia um único resultado, e o `v-if` do menu
        // destruía o próprio botão que o ancora — o menu fechava sozinho.
        this.listTotal = Number.isNaN(count) ? this.items.length : count;
      } catch (error) {
        console.log("AssociatedEntities:loadPage", error);
      } finally {
        this.loadingList = false;
      }
    },

    handleSelect(item) {
      if (!this.canOpen || !item) return;

      this.menu = false;
      this.$emit("select", { kind: this.entityKind, entity: item });
    },
  },
};
</script>

<style scoped lang="scss">
.associated-entities {
  &__name--link {
    cursor: pointer;
    text-decoration: underline;
  }

  &__list {
    max-height: 14rem;
    overflow-y: auto;
  }

  // Item que não abre nada: mesma cor do menu clicável, sem o cursor e o
  // realce de hover que prometeriam uma ação que não existe.
  &__item--static {
    cursor: default;

    &::before {
      display: none;
    }
  }
}
</style>
